import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const Section = styled.div`
  margin-bottom: 24px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const TitleWrapper = styled.div`
  flex: 1;
`

const DraftBadge = styled.div`
  background: #fff3e0;
  color: #f57c00;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
`

const InfoBox = styled.div`
  background-color: #fff9e6;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.5;
  border-radius: 8px;
  color: #7a5c00;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`

const InfoRow = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 16px;
`

const InfoItem = styled.div`
  flex: 1;
`

const Label = styled(Typography)`
  color: ${({ theme }) => theme?.gray?.dark || '#666'};
  margin-bottom: 4px;
`

const Value = styled(Typography)`
  font-weight: 600;
`

const DraftInsuranceCards = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { patient } = usePatientContext()
  const insuranceForms = patient?.insuranceForms || []

  if (insuranceForms.length === 0) {
    return null
  }

  const handleAction = insuranceForm => {
    navigate(`/v2/insurance-form/${patient.hashid}`, {
      state: { insuranceForm, patient },
    })
  }

  return (
    <>
      {insuranceForms.map((insuranceForm, index) => {
        const isDraft = insuranceForm.status === 'draft'
        const insuranceCompany = insuranceForm?.patient?.insuranceCompany
        const memberId = insuranceForm?.patient?.memberId

        return (
          <Section key={index}>
            <SectionHeader>
              <TitleWrapper>
                <Typography variant="h4" fontSize="14px" fontWeight="600">
                  {insuranceCompany || 'Insurance Information'}
                  {isDraft && (
                    <DraftBadge>
                      <WarningAmberIcon sx={{ fontSize: 12 }} />
                      DRAFT
                    </DraftBadge>
                  )}
                </Typography>
                {memberId && (
                  <Typography
                    variant="h5"
                    fontSize="12px"
                    color="textSecondary"
                    sx={{ mt: '4px' }}
                  >
                    Member #{memberId}
                  </Typography>
                )}
              </TitleWrapper>
            </SectionHeader>

            {isDraft && (
              <InfoBox>
                <WarningAmberIcon
                  sx={{ fontSize: 18, color: '#f57c00', flexShrink: 0 }}
                />
                <div>
                  Patient has provided insurance information, however, to use
                  it, a staff member must review and complete the form if
                  needed.
                </div>
              </InfoBox>
            )}

            <InfoRow>
              <InfoItem>
                <Label variant="h6" fontSize="12px" theme={theme.palette}>
                  Patient Name
                </Label>
                <Value variant="h5" fontSize="14px">
                  {insuranceForm?.patient?.firstName || 'Not informed'}{' '}
                  {insuranceForm?.patient?.lastName || ''}
                </Value>
              </InfoItem>
              <InfoItem>
                <Label variant="h6" fontSize="12px" theme={theme.palette}>
                  Insurance Company
                </Label>
                <Value variant="h5" fontSize="14px">
                  {insuranceCompany || 'Not informed'}
                </Value>
              </InfoItem>
            </InfoRow>

            <InfoRow>
              <InfoItem>
                <Label variant="h6" fontSize="12px" theme={theme.palette}>
                  Group Number
                </Label>
                <Value variant="h5" fontSize="14px">
                  {insuranceForm?.patient?.planGroupNumber || 'Not informed'}
                </Value>
              </InfoItem>
              <InfoItem>
                <Label variant="h6" fontSize="12px" theme={theme.palette}>
                  Subscriber ID
                </Label>
                <Value variant="h5" fontSize="14px">
                  {insuranceForm?.patient
                    ?.staffConsultPolicyHolderSubscriberId || 'Not informed'}
                </Value>
              </InfoItem>
            </InfoRow>

            {insuranceForm?.subscriber?.firstName && (
              <InfoRow>
                <InfoItem>
                  <Label variant="h6" fontSize="12px" theme={theme.palette}>
                    Subscriber Name
                  </Label>
                  <Value variant="h5" fontSize="14px">
                    {insuranceForm?.subscriber?.firstName}{' '}
                    {insuranceForm?.subscriber?.lastName || ''}
                  </Value>
                </InfoItem>
                <InfoItem>
                  <Label variant="h6" fontSize="12px" theme={theme.palette}>
                    Relationship
                  </Label>
                  <Value variant="h5" fontSize="14px">
                    {insuranceForm?.subscriber?.relationToPatient
                      ? insuranceForm.subscriber.relationToPatient
                          .charAt(0)
                          .toUpperCase() +
                        insuranceForm.subscriber.relationToPatient.slice(1)
                      : 'Not informed'}
                  </Value>
                </InfoItem>
              </InfoRow>
            )}

            <Button
              variant="contained"
              onClick={() => handleAction(insuranceForm)}
              color="primary"
              fullWidth
              sx={{ mt: '8px', mx: 0 }}
            >
              {isDraft ? 'Review Insurance' : 'Edit Insurance'}
            </Button>
          </Section>
        )
      })}
    </>
  )
}

export default DraftInsuranceCards
